/**
 * ErrorState (treatment A) - the default for a read failure that leaves a
 * region with nothing to show.
 *
 * Wraps <PageState> with the shared `errorStateProps` mapping so every error
 * page names its cause, uses the right visual (`denied` for permission kinds),
 * and only offers a retry when a retry could actually succeed.
 *
 *   <ErrorState error={query.error} noun="policies" onRetry={query.refetch} />
 *
 * Callers pass `onRetry` unconditionally - this component decides whether a
 * retry is honest for the given kind and drops the button when it is not. That
 * is deliberate: it is the one place that judgement lives.
 */
import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../Button';
import { PageState } from './PageState';
import { errorStateProps, type ErrorStateContext } from './error-state';

export type ErrorStateProps = ErrorStateContext & {
  error: unknown;
  /** Refetch for the failed query. Rendered only when the kind is retryable. */
  onRetry?: () => void;
  /** Route to navigate to for `not_found` / the `backLabel` action. */
  backTo?: string;
  /** Stretch to fill the parent (see PageState `fill`). */
  fill?: boolean;
  className?: string;
};

export function ErrorState({
  error,
  onRetry,
  backTo,
  fill,
  className,
  ...ctx
}: ErrorStateProps): ReactElement {
  const navigate = useNavigate();
  const p = errorStateProps(error, ctx);
  const devCrashDetail =
    import.meta.env.DEV && p.kind === 'crash'
      ? error instanceof Error
        ? error.message
        : String(error)
      : null;

  // Retry is only offered when the mapping says it could work. `action` is the
  // non-retry alternative (re-auth, navigate away, reload) when one exists.
  const retry =
    p.retryable && onRetry ? (
      <Button size="sm" variant="secondary" onClick={onRetry}>
        {p.retryLabel}
      </Button>
    ) : null;

  const action = p.action ? (
    <Button
      size="sm"
      variant={p.action.kind === 'reauth' ? 'primary' : 'secondary'}
      onClick={() => {
        if (p.action?.kind === 'reauth') {
          navigate('/auth');
        } else if (p.action?.kind === 'navigate' && backTo) {
          navigate(backTo);
        } else {
          // `reload` - remount the view by refetching when we can, else a hard
          // reload as the last resort (crash case, where no query exists).
          if (onRetry) onRetry();
          else window.location.reload();
        }
      }}
    >
      {p.action.label}
    </Button>
  ) : null;

  const cta =
    retry || action ? (
      <div className="flex flex-wrap items-center justify-center gap-2.5">
        {action}
        {retry}
      </div>
    ) : null;

  return (
    <PageState
      state={p.state}
      icon={p.icon}
      headline={p.headline}
      body={
        devCrashDetail ? (
          <>
            {p.body}
            <code className="text-aq-bad mt-2 block font-mono text-xs">{devCrashDetail}</code>
          </>
        ) : (
          p.body
        )
      }
      cta={cta}
      fill={fill}
      className={className}
    />
  );
}
